// ###### IMPORTS
import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'react-hot-toast';
import { getPluggyTransactions } from 'views/finance/moduleServices/pluggy_services';

// ###### COMPONENT
const CardPluggyTransactions = ({
  accounts,
  selectedAccountId,
  setSelectedAccountId,
  loading,
  setLoading
}) => {
  // ###### LOCAL STATES
  const [form, setForm] = useState({
    page_size: '',
    from_date: '',
    to_date: '',
  });

  const [transactions, setTransactions] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  const [timezoneOffset, setTimezoneOffset] = useState(-3);

  // ###### UTILS
  const adjustTimezone = (dateStr) => {
    const d = new Date(dateStr);
    return new Date(d.getTime() + timezoneOffset * 60 * 60 * 1000);
  };

  // ###### HANDLERS
  const handleGetTransactions = async () => {
    setTransactions([]);
    try {
      setLoading(prev => ({ ...prev, transactions: true }));
      const res = await getPluggyTransactions({
        account_id: selectedAccountId,
        page_size: form.page_size,
        from_date: form.from_date,
        to_date: form.to_date
      });
      setTransactions(res || []);
      if (!res || res.length === 0) {
        toast.error('Pluggy returned 0 transactions for this account and date range.', { duration: 6000 });
      } else {
        toast.success(`Loaded ${res.length} transaction(s)`);
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      toast.error('Failed to fetch transactions');
    } finally {
      setLoading(prev => ({ ...prev, transactions: false }));
    }
  };

  const downloadJSON = () => {
    const blob = new Blob([JSON.stringify(transactions, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'transactions.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadExcel = () => {
    const data = transactions.map(tx => {
      const adjustedDate = adjustTimezone(tx.date);
      return {
        date: adjustedDate.toISOString().split('T')[0],
        hour: adjustedDate.toISOString().split('T')[1]?.split('.')[0],
        description: tx.description || 'none',
        descriptionRaw: tx.descriptionRaw || 'none',
        amount: tx.amount ?? 'none',
        currencyCode: tx.currencyCode || 'none',
        category: tx.category || 'none',
        categoryId: tx.categoryId || 'none',
        type: tx.type || 'none',
        status: tx.status || 'none',
        id: tx.id || 'none',
        cardNumber: tx.creditCardMetadata?.cardNumber || 'none',
        'creditCardMetadata.payeeMCC': tx.creditCardMetadata?.payeeMCC || 'none',
        'creditCardMetadata.purchaseDate': tx.creditCardMetadata?.purchaseDate || 'none',
        'creditCardMetadata.totalInstallments': tx.creditCardMetadata?.totalInstallments || 'none',
        'creditCardMetadata.installmentNumber': tx.creditCardMetadata?.installmentNumber || 'none'
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Transactions');
    XLSX.writeFile(workbook, 'transactions.xlsx');
  };

  // ###### RETURN VIEW
  return (
    <div className="bg-white p-6 mt-2 rounded-2xl shadow flex-1">
      <h2 className="text-xl font-semibold">Pluggy Transactions</h2>
      <div className="mt-4 flex flex-col gap-4">
        {accounts.length > 0 ? (
          <>
            <div>
              <select
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
                className="border p-2 rounded w-full"
              >
                <option value="">-- Select Account --</option>
                {accounts.map(acc => (
                  <option key={acc.id} value={acc.id}>
                    {acc.name || acc.id}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <input
                type="number"
                min={1}
                value={form.page_size || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setForm({ ...form, page_size: val === '' ? '' : parseInt(val) });
                }}
                className="border p-2 rounded"
                placeholder="Page size"
              />
              <input
                type="date"
                value={form.from_date}
                onChange={(e) => setForm({ ...form, from_date: e.target.value })}
                className="border p-2 rounded"
              />
              <input
                type="date"
                value={form.to_date}
                onChange={(e) => setForm({ ...form, to_date: e.target.value })}
                className="border p-2 rounded"
              />
            </div>

            <div className="mt-2">
              <label className="text-sm text-gray-700 font-medium mb-1 block">
                Selected time zone to convert:
              </label>
              <select
                value={timezoneOffset}
                onChange={(e) => setTimezoneOffset(parseInt(e.target.value))}
                className="border p-2 rounded w-full"
              >
                <option value="-5">GMT-5</option>
                <option value="-4">GMT-4</option>
                <option value="-3">GMT-3 (Brasília)</option>
                <option value="-2">GMT-2</option>
                <option value="0">GMT+0 (UTC)</option>
                <option value="1">GMT+1</option>
                <option value="2">GMT+2</option>
                <option value="3">GMT+3</option>
              </select>
            </div>

            <button
              onClick={handleGetTransactions}
              disabled={loading.transactions || !selectedAccountId}
              className="mt-2 bg-green-800 text-white px-4 py-2 rounded-lg w-fit"
            >
              {loading.transactions ? 'Loading transactions...' : 'Get Transactions'}
            </button>
          </>
        ) : (
          <p className="text-sm text-gray-500">No available accounts yet</p>
        )}

        {transactions.length > 0 && (
          <div className="mt-4">
            <div className="flex gap-4 mb-2">
              <button
                className={`px-4 py-2 text-sm font-medium ${viewMode === 'table' ? 'border-b-2 border-green-800 text-green-800' : 'text-gray-600'}`}
                onClick={() => setViewMode('table')}
              >
                Table
              </button>
              <button
                className={`px-4 py-2 text-sm font-medium ${viewMode === 'json' ? 'border-b-2 border-green-800 text-green-800' : 'text-gray-600'}`}
                onClick={() => setViewMode('json')}
              >
                JSON
              </button>
            </div>

            {viewMode === 'json' ? (
              <>
                <pre className="bg-gray-100 p-2 rounded text-sm max-h-64 overflow-auto">
                  {JSON.stringify(transactions, null, 2)}
                </pre>
                <div className="flex gap-2 mt-2">
                  <button onClick={downloadExcel} className="text-sm bg-green-700 text-white px-3 py-1 rounded hover:bg-green-800 transition">
                    Download Excel
                  </button>
                  <button onClick={downloadJSON} className="text-sm bg-blue-700 text-white px-3 py-1 rounded hover:bg-blue-800 transition">
                    Download JSON
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="overflow-auto max-h-64 border rounded">
                  <table className="min-w-full text-sm text-gray-700">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        {[
                          'date', 'hour', 'description', 'descriptionRaw', 'amount', 'currencyCode',
                          'category', 'categoryId', 'type', 'status', 'id', 'cardNumber',
                          'payeeMCC', 'purchaseDate', 'totalInstallments', 'installmentNumber'
                        ].map(col => (
                          <th key={col} className="px-2 py-1 text-left border-b">{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((tx, idx) => {
                        const adjustedDate = adjustTimezone(tx.date);
                        return (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="px-2 py-1 whitespace-nowrap">{adjustedDate.toISOString().split('T')[0]}</td>
                            <td className="px-2 py-1">{adjustedDate.toISOString().split('T')[1]?.split('.')[0]}</td>
                            <td className="px-2 py-1">{tx.description || 'none'}</td>
                            <td className="px-2 py-1">{tx.descriptionRaw || 'none'}</td>
                            <td className="px-2 py-1">{tx.amount ?? 'none'}</td>
                            <td className="px-2 py-1">{tx.currencyCode || 'none'}</td>
                            <td className="px-2 py-1">{tx.category || 'none'}</td>
                            <td className="px-2 py-1">{tx.categoryId || 'none'}</td>
                            <td className="px-2 py-1">{tx.type || 'none'}</td>
                            <td className="px-2 py-1">{tx.status || 'none'}</td>
                            <td className="px-2 py-1 whitespace-nowrap">{tx.id || 'none'}</td>
                            <td className="px-2 py-1">{tx.creditCardMetadata?.cardNumber || 'none'}</td>
                            <td className="px-2 py-1">{tx.creditCardMetadata?.payeeMCC || 'none'}</td>
                            <td className="px-2 py-1">{tx.creditCardMetadata?.purchaseDate || 'none'}</td>
                            <td className="px-2 py-1">{tx.creditCardMetadata?.totalInstallments || 'none'}</td>
                            <td className="px-2 py-1">{tx.creditCardMetadata?.installmentNumber || 'none'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="flex gap-2 mt-2">
                  <button onClick={downloadExcel} className="text-sm bg-green-700 text-white px-3 py-1 rounded hover:bg-green-800 transition">
                    Download Excel
                  </button>
                  <button onClick={downloadJSON} className="text-sm bg-blue-700 text-white px-3 py-1 rounded hover:bg-blue-800 transition">
                    Download JSON
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CardPluggyTransactions;
